import { useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import { api, ApiError, PublicPortalSite } from "../lib/api.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";

function statusPill(s: PublicPortalSite["status"]) {
  if (s === "active") return <span className="pill pill-ok">Active</span>;
  return <span className="pill pill-warn">Draft</span>;
}

function visibilityPill(v: PublicPortalSite["visibility"]) {
  if (v === "public") return <span className="pill pill-info">Public</span>;
  return <span className="pill pill-err">Restricted</span>;
}

function authPill(a: PublicPortalSite["authMode"]) {
  if (a === "none")        return <span className="pill pill-muted">Open</span>;
  if (a === "password")    return <span className="pill pill-info">Password</span>;
  return <span className="pill pill-info">Invite only</span>;
}

export default function Portals() {
  const [rows, setRows] = useState<PublicPortalSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const t = useTableState(rows as unknown as Array<Record<string, unknown>>, {
    searchFields: ["name", "slug", "workspaceId", "status", "visibility", "authMode", "audience"],
    initialPageSize: 25
  });

  useEffect(() => {
    let cancelled = false;
    api.publicPortal()
      .then((d) => { if (!cancelled) setRows(d.portals ?? []); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Public portals</h1>
          <p>Externally-facing surfaces that expose selected datasets.</p>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? <SkeletonTable rows={5} columns={4} /> :
       rows.length === 0 ? (
        <div className="empty">
          <p><strong>No public portals yet.</strong></p>
          <p className="muted">
            Portals let stakeholders view aggregate reports without signing in.
            Publish a <a href="/app/datasets">dataset</a> first, then create a portal backed by it.
          </p>
        </div>
       ) : (
        <div className="table-scroll">
          <TableToolbar
            search={t.search} setSearch={t.setSearch}
            page={t.page} setPage={t.setPage}
            pageSize={t.pageSize} setPageSize={t.setPageSize}
            totalPages={t.totalPages} totalRows={t.totalRows}
            hideExport
            searchPlaceholder="Search portals…"
          />
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Workspace</th>
                <th>Status</th>
                <th>Visibility</th>
                <th>Auth</th>
                <th>Datasets</th>
                <th>Audience</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {(t.paged as unknown as PublicPortalSite[]).map((p) => (
                <tr key={p.id}>
                  <td><strong>{p.name}</strong></td>
                  <td><code>{p.slug}</code></td>
                  <td><code>{p.workspaceId}</code></td>
                  <td>{statusPill(p.status)}</td>
                  <td>{visibilityPill(p.visibility)}</td>
                  <td>{authPill(p.authMode)}</td>
                  <td>{p.datasetIds.length}</td>
                  <td className="soft">{p.audience}</td>
                  <td className="soft">{p.updatedAt ? new Date(p.updatedAt).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}
    </div>
  );
}
